import { createSelector } from '@reduxjs/toolkit';
import type { LessonModel } from '../Models/LessonModel';
import type { StudentAbsenceModel, StudentModel, StudentSlotModel } from '../Models/StudentModel';
import { StudentKind } from '../Models/enums';
import { isCancelled, isCollected, isOwed, isScheduledLesson, localDateString } from '../Utils/LessonStatusUtil';
import { amountValue } from '../Utils/MoneyUtil';
import type { RootState } from './store';

/**
 * The derived numbers, in one place.
 *
 * Every one of them runs a lesson through `isOwed` with that student's slots,
 * because a lesson's duration is what decides when it has ended and therefore
 * when it becomes debt. A component that adds up `!paid` amounts on its own is
 * how next month's lessons end up in this month's overdue total.
 */

const selectStudentItems = (state: RootState) => state.students.items;
const selectLessonItems = (state: RootState) => state.lessons.items;

export const selectSlotsByStudent = createSelector(
  [selectStudentItems],
  (students): Map<string, StudentSlotModel[]> =>
    new Map(students.map(student => [student.name, student.slots ?? []]))
);

export const selectAbsencesByStudent = createSelector(
  [selectStudentItems],
  (students): Map<string, StudentAbsenceModel[]> =>
    new Map(students.map(student => [student.name, student.absences ?? []]))
);

export const selectActiveStudents = createSelector(
  [selectStudentItems],
  (students): StudentModel[] => students.filter(student => !student.archived)
);

/**
 * The people actually taught.
 *
 * An organization is a row in the same table, because that is what gives it
 * debt, reminders and receipts for free. It is not a student, though, so
 * anything that counts students or offers a list of them to teach reads this
 * rather than the raw roster: a tutor with eight players billed to a
 * conservatory teaches eight students, not nine.
 */
export const selectTaughtStudents = createSelector(
  [selectStudentItems],
  (students): StudentModel[] => students.filter(student => student.kind !== StudentKind.Organization)
);

/** The bodies that pay without being taught. The other half of the roster. */
export const selectOrganizations = createSelector(
  [selectStudentItems],
  (students): StudentModel[] => students.filter(student => student.kind === StudentKind.Organization)
);

/** Taught, and not retired from the roster. What a picker should offer. */
export const selectActiveTaughtStudents = createSelector(
  [selectTaughtStudents],
  (students): StudentModel[] => students.filter(student => !student.archived)
);

export interface LessonTotals {
  /** Money actually received. */
  collected: number;
  /** Charges that are due and unpaid. */
  owed: number;
  /** Unpaid lessons that have not happened yet. Income, not debt. */
  scheduled: number;
}

function totalsFor(
  lessons: readonly LessonModel[],
  slotsByStudent: Map<string, StudentSlotModel[]>,
  now: Date
): LessonTotals {
  const totals: LessonTotals = { collected: 0, owed: 0, scheduled: 0 };
  for (const lesson of lessons) {
    const slots = slotsByStudent.get(lesson.student) ?? [];
    const amount = amountValue(lesson.amount);
    if (isCollected(lesson)) totals.collected += amount;
    else if (isOwed(lesson, slots, now)) totals.owed += amount;
    else if (isScheduledLesson(lesson, slots, now)) totals.scheduled += amount;
  }
  return totals;
}

export const selectTotals = createSelector(
  [selectLessonItems, selectSlotsByStudent],
  (lessons, slotsByStudent): LessonTotals => totalsFor(lessons, slotsByStudent, new Date())
);

export const selectDebtByStudent = createSelector(
  [selectLessonItems, selectSlotsByStudent],
  (lessons, slotsByStudent): Map<string, number> => {
    const now = new Date();
    const debt = new Map<string, number>();
    for (const lesson of lessons) {
      if (!isOwed(lesson, slotsByStudent.get(lesson.student) ?? [], now)) continue;
      debt.set(lesson.student, (debt.get(lesson.student) ?? 0) + amountValue(lesson.amount));
    }
    return debt;
  }
);

/** Everything dated today, cancelled or not: what books the day's hours. */
export const selectTodayLessons = createSelector(
  [selectLessonItems],
  (lessons): LessonModel[] => {
    const today = localDateString();
    return lessons.filter(lesson => lesson.date === today);
  }
);

/**
 * Today's appointments. A cancelled lesson is not one, so the dashboard does
 * not list it, but it still counts above so its recurring hour stays taken.
 */
export const selectTodayAppointments = createSelector(
  [selectTodayLessons],
  (lessons): LessonModel[] => lessons.filter(lesson => !isCancelled(lesson))
);

/**
 * The next lesson still to come. A lesson later today counts, which is why
 * this compares the time as well as the day rather than starting tomorrow.
 */
export const selectNextLesson = createSelector(
  [selectLessonItems, selectSlotsByStudent],
  (lessons, slotsByStudent): LessonModel | null => {
    const now = new Date();
    const today = localDateString(now);
    const upcoming = lessons
      .filter(lesson => {
        if (lesson.date > today) return true;
        if (lesson.date < today) return false;
        return isScheduledLesson(lesson, slotsByStudent.get(lesson.student) ?? [], now);
      })
      .sort((a, b) =>
        a.date === b.date
          ? (a.time ?? '').localeCompare(b.time ?? '')
          : a.date.localeCompare(b.date));
    return upcoming[0] ?? null;
  }
);

export const selectLessonsForStudent = (student: string) =>
  createSelector([selectLessonItems], (lessons): LessonModel[] =>
    lessons.filter(lesson => lesson.student === student));
